import { auth } from "@/lib/firebase"
import { useQuery } from "@tanstack/react-query"

// 1. Define the expected response structure from the backend
interface DocumentMarkdownResponse {
	markdown_content: string | null // Match the backend response model
}

// 2. Define the fetch function
export const fetchDocumentMarkdown = async (
	caseId: string,
	documentId: string
): Promise<string | null> => {
	const apiUrl = `${
		import.meta.env.VITE_API_URL
	}/api/cases/${caseId}/documents/${documentId}/markdown/`

	const user = auth.currentUser
	if (!user) {
		throw new Error("User not found.")
	}
	const token = await user.getIdToken()

	const response = await fetch(apiUrl, {
		headers: { Authorization: `Bearer ${token}` },
	})

	if (!response.ok) {
		// If the status is 404, it means markdown is not found or the document itself is not found.
		// We can return null in this case, as it's an expected scenario (markdown might not be generated yet).
		if (response.status === 404) {
			return null
		}
		// For other errors, throw an error.
		const errorData = await response.text()
		throw new Error(
			`Failed to fetch document markdown: ${response.status} ${response.statusText} - ${errorData}`
		)
	}

	const data: DocumentMarkdownResponse = await response.json()
	return data.markdown_content // Return only the markdown string or null
}

// 3. Create the custom hook using useQuery
export const useDocumentAsMarkdown = (caseId?: string, documentId?: string) => {
	return useQuery<string | null, Error>({
		queryKey: ["documentMarkdown", caseId, documentId], // Unique key for this query
		queryFn: () => {
			if (!caseId || !documentId) {
				// If IDs are not provided, return null immediately without fetching
				return Promise.resolve(null)
			}
			return fetchDocumentMarkdown(caseId, documentId)
		},
		enabled: !!caseId && !!documentId, // Only run the query if IDs are provided
		staleTime: 5 * 60 * 1000, // Optional: Data is considered fresh for 5 minutes
		refetchOnWindowFocus: false, // Optional: Prevent refetching on window focus
		// No automatic refetching needed unless specifically required
	})
}
